import MsalAuthService from "./msalAuth.service";
import environment from "environment";
import { Shared } from "utility/shared";

export type RequestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiResponse<T> {
	statusCode: number;
	responseObj?: T;
	error?: string;
	isBadRequest?: boolean;
}

export class BaseService {
	/**
	 * Function to call and parse external API
	 *
	 * @protected
	 * @template
	 * @param {string} route - API route
	 * @param {RequestMethod} method - Calling method
	 * @param {URLSearchParams} [urlParams] - Query parameters to pass in API
	 * @param {Record<string, unknown>} [requestData] - body parameters to pass in API
	 * @param {boolean} [skipReadingResponseBody] - is response body required to be skipped (in case of getting 204 or 200 with no response)
	 * @param {boolean} [isFileUpload] - if true, skip content type in API
	 * @param {boolean} [fetchToken] - if false, skip fetch token call and do not pass token in Authorization
	 * @returns {Promise<T>} - Promise with defined datatype
	 * @memberof BaseService
	 */
	protected async callApi<T>(
		route: string,
		method: RequestMethod,
		urlParams?: URLSearchParams,
		requestData?: object | any[],
		skipReadingResponseBody?: boolean,
		isFileUpload?: boolean,
		fetchToken = true,
		isTextResponse = false
	): Promise<T> {
		try {
			let headers: Record<string, string> = {};
			if (fetchToken) {
				const token = Shared.isExternalUserLoggedIn(false)
					? await Shared.getExternalUserAuthToken()
					: await MsalAuthService.getAuthToken();
				if (!token) {
					return await this.response({
						statusCode: 401,
						error: "Session is expired, please refresh you page",
					});
				}
				headers = {
					Authorization: `Bearer ${token}`,
				};
			}
			let body: string | undefined | FormData;
			const url = `${environment.apiUrl}/${route}${
				urlParams ? `?${urlParams.toString()}` : ""
			}`;

			if (requestData) {
				if (method === "GET") {
					return await this.response({
						statusCode: 500,
						error: "Invalid request, GET requests cannot have a body",
					});
				}
				if (!isFileUpload) {
					headers["Content-Type"] =
						method === "PATCH"
							? "application/json-patch+json"
							: "application/json";
					body = JSON.stringify(requestData);
				} else {
					body = requestData as FormData;
				}
			}

			const res = await fetch(url, {
				method,
				headers,
				body,
			});
			if (res.ok) {
				try {
					if (skipReadingResponseBody) {
						return await this.response({
							statusCode: res.status,
						});
					}
					// if api returns no content
					if (res.status === 204) {
						return await this.response({
							statusCode: res.status,
						});
					}

					const resData = await res.json();
					// res = "OK", but internal API error, response returned with 400 and error message
					if (resData.statusCode === 400) {
						return this.response({
							statusCode: resData.statusCode,
							error: resData.messages,
							responseObj: resData.result as T,
							isBadRequest: true,
						});
					}
					return await this.response({
						statusCode: res.status,
						responseObj: isTextResponse ? resData : (resData.result as T),
					});
				} catch (err) {
					return this.response({
						statusCode: res.status,
						error: "failed to parse response returned by server",
					});
				}
			} else {
				const resData = await res.json();
				if (resData.statusCode === 400) {
					return this.response({
						statusCode: resData.statusCode,
						error: resData.messages,
						responseObj: resData.result as T,
						isBadRequest: true,
					});
				}
				return await this.response({
					statusCode: res.status,
					error: res.statusText,
					responseObj: res as unknown as T,
				});
			}
		} catch (error) {
			const err = error as any;
			return this.response({
				statusCode: err.statusCode,
				error: err.error,
				responseObj: err.responseObj,
			});
		}
	}

	/**
	 * Function to manage promises returns from API calling
	 *
	 * @protected
	 * @template T
	 * @param {ApiResponse<T>} response
	 * @returns {Promise<T>}
	 * @memberof BaseService
	 */
	protected response<T>(response: ApiResponse<T>): Promise<T> {
		if (response.statusCode === 401) {
			return Promise.reject({
				statusCode: response.statusCode,
				error: "Session is expired, please refresh you page",
			});
		}
		if (response.statusCode >= 200 && response.statusCode < 300) {
			return Promise.resolve(response.responseObj as T);
		}
		if (response.responseObj || response.isBadRequest) {
			return Promise.reject({
				statusCode: response.statusCode,
				error: response.error,
				responseObj: response.responseObj,
				badRequestError:
					response.isBadRequest && response.error
						? Array.isArray(response.error) && response.error.length > 0
							? response.error[0]
							: response.error
						: undefined,
			});
		}
		return Promise.reject(response);
	}
}
